import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CreateAudienceAdminPayload,
  type CreateAudienceAdminResponse,
  type CreateAudienceUsersAdminPayload,
  type CreateAudienceUsersAdminResponse,
  type DeleteAudienceUsersAdminPayload,
  type GetAudienceAdminListQuery,
  type GetAudiencePublicListQuery,
  type GetAudienceUsersAdminListQuery,
  type ListAudienceUsersQuery,
  type ListAudiencesQuery,
  type SearchAudienceAdminListQuery,
  type UpdateAudienceAdminPayload,
  type UpdateAudienceAdminResponse,
  createAudienceAdmin,
  createAudienceUsersAdmin,
  deleteAudienceUsersAdmin,
  getAudienceAdminList,
  getAudiencePublicList,
  getAudienceUsersAdminList,
  listAudienceUsers,
  listAudiences,
  searchAudienceAdminList,
  updateAudienceAdmin,
} from '~/services/PromoService';

export const audienceQueryKeys = {
  all: ['audiences'] as const,
  lists: () => [...audienceQueryKeys.all, 'list'] as const,
  list: (query: ListAudiencesQuery) => [...audienceQueryKeys.lists(), query] as const,
};

export const audienceUserQueryKeys = {
  all: ['audience-users'] as const,
  lists: () => [...audienceUserQueryKeys.all, 'list'] as const,
  list: (query: ListAudienceUsersQuery) => [...audienceUserQueryKeys.lists(), query] as const,
};

export const audienceAdminQueryKeys = {
  all: ['audience-admin'] as const,
  lists: () => [...audienceAdminQueryKeys.all, 'list'] as const,
  list: (query: GetAudienceAdminListQuery) => [...audienceAdminQueryKeys.lists(), query] as const,
};

export const audienceAdminSearchQueryKeys = {
  all: ['audience-admin-search'] as const,
  lists: () => [...audienceAdminSearchQueryKeys.all, 'list'] as const,
  list: (query: SearchAudienceAdminListQuery) => [...audienceAdminSearchQueryKeys.lists(), query] as const,
};

export const audiencePublicQueryKeys = {
  all: ['audience-public'] as const,
  lists: () => [...audiencePublicQueryKeys.all, 'list'] as const,
  list: (query: GetAudiencePublicListQuery) => [...audiencePublicQueryKeys.lists(), query] as const,
};

type QueryParams<TQuery> = {
  query: TQuery;
  enabled?: boolean;
};

export function useListAudiences({ query, enabled = true }: QueryParams<ListAudiencesQuery>) {
  return useQuery({
    queryKey: audienceQueryKeys.list(query),
    queryFn: async ({ signal }) => listAudiences(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useListAudienceUsers({ query, enabled = true }: QueryParams<ListAudienceUsersQuery>) {
  return useQuery({
    queryKey: audienceUserQueryKeys.list(query),
    queryFn: async ({ signal }) => listAudienceUsers(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useGetAudienceUsersAdminList({ query, enabled = true }: QueryParams<GetAudienceUsersAdminListQuery>) {
  return useQuery({
    queryKey: audienceUserQueryKeys.list(query),
    queryFn: async ({ signal }) => getAudienceUsersAdminList(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useGetAudienceAdminList({ query, enabled = true }: QueryParams<GetAudienceAdminListQuery>) {
  return useQuery({
    queryKey: audienceAdminQueryKeys.list(query),
    queryFn: async ({ signal }) => getAudienceAdminList(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useSearchAudienceAdminList({ query, enabled = true }: QueryParams<SearchAudienceAdminListQuery>) {
  return useQuery({
    queryKey: audienceAdminSearchQueryKeys.list(query),
    queryFn: async ({ signal }) => searchAudienceAdminList(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useGetAudiencePublicList({ query, enabled = true }: QueryParams<GetAudiencePublicListQuery>) {
  return useQuery({
    queryKey: audiencePublicQueryKeys.list(query),
    queryFn: async ({ signal }) => getAudiencePublicList(query, { signal }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCreateAudienceAdmin() {
  const queryClient = useQueryClient();

  return useMutation<CreateAudienceAdminResponse, Error, CreateAudienceAdminPayload>({
    mutationFn: async (payload) => createAudienceAdmin(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: audienceQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceUserQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
    },
  });
}

type UpdateAudienceAdminVars = {
  audienceId: string;
  payload: UpdateAudienceAdminPayload;
};

export function useUpdateAudienceAdmin() {
  const queryClient = useQueryClient();

  return useMutation<UpdateAudienceAdminResponse, Error, UpdateAudienceAdminVars>({
    mutationFn: async ({ audienceId, payload }) => updateAudienceAdmin(audienceId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: audienceQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceUserQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
    },
  });
}

export function useCreateAudienceUsersAdmin() {
  const queryClient = useQueryClient();

  return useMutation<CreateAudienceUsersAdminResponse, Error, CreateAudienceUsersAdminPayload>({
    mutationFn: async (payload) => createAudienceUsersAdmin(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: audienceQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceUserQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
    },
  });
}

export function useDeleteAudienceUsersAdmin() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, DeleteAudienceUsersAdminPayload>({
    mutationFn: async (payload) => deleteAudienceUsersAdmin(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: audienceQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceUserQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
    },
  });
}
